import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { FileHandleRef, PlatformBridge, SearchHit, SearchMatch } from '@slate/core';
import { Search, Text, X } from 'lucide-react';
import { useDocStore } from '../store/docStore';
import { useFsDragStore } from '../store/fsDragStore';
import { openPath } from '../store/docActions';
import { isImageFile } from '../util/fileType';
import { FileTypeIcon } from '../icons/FileTypeIcon';

export interface SidebarSearchProps {
  platform: PlatformBridge;
  /** All workspace roots to search across. */
  roots: FileHandleRef[];
  /** Notifies the parent when a (non-empty) search is active so it can hide the tree. */
  onActiveChange?: (active: boolean) => void;
}

const DEBOUNCE_MS = 200;
/** Per-level indent step, matching the reduced indentation in `FileTree`. */
const INDENT_STEP = 11;
const INDENT_BASE = 8;

/** Strip the workspace-root prefix to get the path relative to that root. */
function relativePath(rootPath: string, path: string): string {
  const root = rootPath.replace(/[/\\]+$/, '');
  if (path.startsWith(`${root}/`)) return path.slice(root.length + 1);
  if (path.startsWith(`${root}\\`)) return path.slice(root.length + 1);
  return path;
}

/** The root whose path prefixes `path` (longest match wins), if any. */
function rootForPath(roots: FileHandleRef[], path: string): FileHandleRef | undefined {
  let best: FileHandleRef | undefined;
  let bestLen = -1;
  for (const root of roots) {
    const rp = root.path;
    if (!rp) continue;
    const norm = rp.replace(/[/\\]+$/, '');
    if (
      (path === norm || path.startsWith(`${norm}/`) || path.startsWith(`${norm}\\`)) &&
      norm.length > bestLen
    ) {
      best = root;
      bestLen = norm.length;
    }
  }
  return best;
}

/** Highlight case-insensitive occurrences of `query` within `text`. */
function highlight(text: string, query: string): ReactNode {
  const q = query.trim();
  if (q === '') return text;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const out: ReactNode[] = [];
  let from = 0;
  let idx = lower.indexOf(needle, from);
  let key = 0;
  while (idx >= 0) {
    if (idx > from) out.push(text.slice(from, idx));
    out.push(
      <mark className="mp-search__hl" key={key++}>
        {text.slice(idx, idx + needle.length)}
      </mark>,
    );
    from = idx + needle.length;
    idx = lower.indexOf(needle, from);
  }
  if (from < text.length) out.push(text.slice(from));
  return out;
}

/**
 * A node in the filtered results tree. Folders may be either a *match* (the
 * folder name matched the query → highlighted) or pure *context* (an ancestor
 * shown only to keep the tree structure intact → dimmed, no highlight).
 */
interface TreeNode {
  /** Full absolute/synthetic path of this node. */
  path: string;
  /** Basename. */
  name: string;
  isDir: boolean;
  /** True when this node itself matched the query (vs. context-only ancestor). */
  matched: boolean;
  /** Content-match snippets for a file hit (when content search was on). */
  contentMatches: SearchMatch[];
  children: TreeNode[];
}

/** A per-root filtered tree, kept with its owning root for labelling. */
interface RootTree {
  root: FileHandleRef;
  nodes: TreeNode[];
}

/**
 * Reconstruct a folder hierarchy from flat hits under one root: every hit, plus
 * all of its ancestor directories up to the root, become nodes. Ancestors that
 * weren't themselves a hit are marked context-only (`matched: false`). The
 * result preserves the tree structure so a deep match shows its parent folders.
 */
function buildTree(rootPath: string, hits: SearchHit[]): TreeNode[] {
  const roots: TreeNode[] = [];
  // Index nodes by full path so ancestors are created once and shared.
  const byPath = new Map<string, TreeNode>();
  // Path-segment separator: prefer the one the rel path actually uses.

  const ensure = (
    fullPath: string,
    name: string,
    isDir: boolean,
    parent: TreeNode | null,
  ): TreeNode => {
    const existing = byPath.get(fullPath);
    if (existing) return existing;
    const node: TreeNode = {
      path: fullPath,
      name,
      isDir,
      matched: false,
      contentMatches: [],
      children: [],
    };
    byPath.set(fullPath, node);
    (parent ? parent.children : roots).push(node);
    return node;
  };

  const norm = rootPath.replace(/[/\\]+$/, '');
  for (const hit of hits) {
    const rel = relativePath(rootPath, hit.path);
    const segs = rel.split(/[/\\]/).filter((s) => s.length > 0);
    if (segs.length === 0) continue;
    // The separator used in this hit's path, so synthetic ancestor paths match
    // the real ones (forward slash on web, native sep on desktop).
    const sep = hit.path.includes('\\') && !hit.path.includes('/') ? '\\' : '/';
    let parent: TreeNode | null = null;
    let acc = norm;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i];
      acc = `${acc}${sep}${seg}`;
      const isLeaf = i === segs.length - 1;
      // Ancestors are always directories; the leaf's kind comes from the hit.
      const isDir = isLeaf ? hit.isDir === true : true;
      const node = ensure(acc, seg, isDir, parent);
      if (isLeaf) {
        node.matched = true;
        if (hit.contentMatches && hit.contentMatches.length > 0) {
          node.contentMatches = hit.contentMatches;
        }
      }
      parent = node;
    }
  }

  sortTree(roots);
  return roots;
}

/** Sort each level directories-first then case-insensitively by name. */
function sortTree(nodes: TreeNode[]): void {
  nodes.sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
  for (const n of nodes) sortTree(n.children);
}

/**
 * Workspace search: a name/content search box at the top of the file-tree area.
 * A non-empty query replaces the live tree(s) with an in-place hierarchical
 * filter — only matching files/folders plus their ancestor folders are shown,
 * fully expanded, with the matched substring highlighted. Async responses are
 * guarded by a per-request token so stale results never overwrite newer ones.
 *
 * Only rendered by `Sidebar` when `platform.searchFiles` exists and
 * `capabilities.search` is true, so the search function is always callable here.
 */
export function SidebarSearch({
  platform,
  roots,
  onActiveChange,
}: SidebarSearchProps): ReactElement {
  const [query, setQuery] = useState('');
  const [content, setContent] = useState(false);
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  const activeId = useDocStore((s) => s.activeId);
  const setPendingJumpLine = useDocStore((s) => s.setPendingJumpLine);

  // Monotonic request token: only the latest request may commit results.
  const reqRef = useRef(0);

  // Stable key over the roots set so the effect doesn't resubscribe spuriously.
  const rootsKey = roots.map((r) => r.id).join(' ');

  useEffect(() => {
    const q = query.trim();
    if (q === '') {
      reqRef.current += 1; // invalidate any in-flight request
      setHits(null);
      setSearching(false);
      return;
    }

    const token = (reqRef.current += 1);
    setSearching(true);
    const timer = setTimeout(() => {
      const search = platform.searchFiles;
      if (typeof search !== 'function') {
        setSearching(false);
        return;
      }
      const searchablePaths = roots.map((r) => r.path).filter((p): p is string => !!p);
      Promise.all(searchablePaths.map((p) => search(p, q, { content })))
        .then((perRoot) => {
          if (reqRef.current !== token) return; // stale — only the latest commits
          // Merge all roots' hits, deduping by absolute path (first seen wins).
          const seen = new Set<string>();
          const merged: SearchHit[] = [];
          for (const list of perRoot) {
            for (const hit of list) {
              if (seen.has(hit.path)) continue;
              seen.add(hit.path);
              merged.push(hit);
            }
          }
          setHits(merged);
          setSearching(false);
        })
        .catch(() => {
          if (reqRef.current !== token) return;
          setHits([]);
          setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `roots` is captured via the stable `rootsKey`; intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, content, platform, rootsKey]);

  const showResults = query.trim() !== '';
  useEffect(() => {
    onActiveChange?.(showResults);
  }, [showResults, onActiveChange]);

  const open = useCallback(
    async (path: string, line?: number): Promise<void> => {
      try {
        // Images open in the viewer; a content-match line jump only applies to
        // text docs (images have no editor/line mapping).
        await openPath(platform, path);
        if (typeof line === 'number' && !isImageFile(path)) setPendingJumpLine(line);
      } catch {
        /* failed read leaves results untouched */
      }
    },
    [platform, setPendingJumpLine],
  );

  const trimmed = query.trim();

  // Group hits by owning root, then build a filtered tree per root that has any.
  // Only roots with matches appear; multi-root groups are labelled by name.
  const rootTrees: RootTree[] = [];
  if (hits && hits.length > 0) {
    const byRootId = new Map<string, { root: FileHandleRef; hits: SearchHit[] }>();
    for (const hit of hits) {
      const owner = rootForPath(roots, hit.path);
      if (!owner) continue;
      let bucket = byRootId.get(owner.id);
      if (!bucket) {
        bucket = { root: owner, hits: [] };
        byRootId.set(owner.id, bucket);
      }
      bucket.hits.push(hit);
    }
    // Preserve the roots' configured order for stable grouping.
    for (const root of roots) {
      const bucket = byRootId.get(root.id);
      if (!bucket || !root.path) continue;
      rootTrees.push({ root, nodes: buildTree(root.path, bucket.hits) });
    }
  }
  const multiRoot = roots.length > 1;

  return (
    <div className="mp-search">
      <div className="mp-search__bar">
        <div className="mp-search__field">
          <Search size={14} className="mp-search__icon" aria-hidden />
          <input
            type="text"
            className="mp-search__input"
            placeholder="Search files…"
            aria-label="Search files"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query !== '' && (
            <button
              type="button"
              className="mp-search__clear"
              aria-label="Clear search"
              title="Clear search"
              onClick={() => setQuery('')}
            >
              <X size={13} aria-hidden />
            </button>
          )}
        </div>
        <button
          type="button"
          className="mp-iconbtn mp-search__toggle"
          aria-label="Search in file contents"
          aria-pressed={content}
          title="Search in file contents"
          onClick={() => setContent((c) => !c)}
        >
          <Text size={15} aria-hidden />
        </button>
      </div>

      {showResults && (
        <div className="mp-search__results" role="list">
          {searching && hits === null && <div className="mp-tree__hint">Searching…</div>}
          {hits !== null && rootTrees.length === 0 && !searching && (
            <div className="mp-tree__hint">No results</div>
          )}
          {rootTrees.map(({ root, nodes }) => (
            <div className="mp-search__roottree" key={root.id}>
              {multiRoot && <div className="mp-search__rootname">{root.name}</div>}
              {nodes.map((node) => (
                <ResultNode
                  key={node.path}
                  node={node}
                  depth={0}
                  query={trimmed}
                  activeId={activeId}
                  onOpen={open}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One node (and its descendants) of the filtered results tree. Fully expanded —
 * it's a filtered view, so every matched branch shows open. Matched nodes get
 * their name highlighted; context-only ancestor folders render dimmed. Files
 * open on click; content-match snippets open + jump to the line. Folders are a
 * no-op (their purpose is structure).
 */
function ResultNode({
  node,
  depth,
  query,
  activeId,
  onOpen,
}: {
  node: TreeNode;
  depth: number;
  query: string;
  activeId: string | null;
  onOpen: (path: string, line?: number) => void | Promise<void>;
}): ReactElement {
  const indent = INDENT_BASE + depth * INDENT_STEP;
  const isFile = !node.isDir;
  const active = isFile && activeId === node.path;
  const name = node.matched ? highlight(node.name, query) : node.name;

  // Name-mode hits are draggable into the editor (App-level subscription
  // handles drop → markdown link/image). Content-mode hits aren't —
  // dragging a line snippet has no clear insert semantic.
  const draggable = isFile && node.contentMatches.length === 0;
  const rowProps = isFile
    ? {
        role: 'button',
        tabIndex: 0,
        draggable,
        onDragStart: draggable
          ? (e: React.DragEvent<HTMLDivElement>) => {
              useFsDragStore.getState().setSources([node.path]);
              try {
                e.dataTransfer.setData(
                  'application/x-slate-fs',
                  JSON.stringify([node.path]),
                );
                e.dataTransfer.effectAllowed = 'copy';
              } catch {
                /* webview may reject custom MIME — ref-store still works */
              }
            }
          : undefined,
        onDragEnd: draggable
          ? () => {
              window.setTimeout(() => useFsDragStore.getState().clearSources(), 250);
            }
          : undefined,
        onClick: () => void onOpen(node.path),
        onKeyDown: (e: ReactKeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void onOpen(node.path);
          }
        },
      }
    : {};

  return (
    <>
      <div
        className={
          isFile
            ? 'mp-tree__row mp-tree__row--file mp-search__node'
            : 'mp-tree__row mp-tree__row--dir mp-search__node'
        }
        data-active={active ? '' : undefined}
        data-context={node.isDir && !node.matched ? '' : undefined}
        style={{ paddingLeft: indent }}
        title={node.path}
        {...rowProps}
      >
        <span className="mp-tree__chevron-gap" aria-hidden />
        <FileTypeIcon
          name={node.name}
          kind={node.isDir ? 'folder' : 'file'}
          size={14}
          className="mp-tree__icon"
        />
        <span className="mp-search__name">{name}</span>
      </div>

      {node.contentMatches.map((m, i) => (
        <div
          key={`${node.path}:${m.line}:${i}`}
          className="mp-search__match"
          role="button"
          tabIndex={0}
          title={`Line ${m.line}`}
          style={{ paddingLeft: indent + 20 }}
          onClick={() => void onOpen(node.path, m.line)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void onOpen(node.path, m.line);
            }
          }}
        >
          <span className="mp-search__lineno">{m.line}</span>
          <span className="mp-search__snippet">{highlight(m.text, query)}</span>
        </div>
      ))}

      {node.children.map((child) => (
        <ResultNode
          key={child.path}
          node={child}
          depth={depth + 1}
          query={query}
          activeId={activeId}
          onOpen={onOpen}
        />
      ))}
    </>
  );
}
